// Optimistic concurrency helpers for the three pilot edit flows (Loan, Product, Customer — see
// KAN-43). Every edit drawer in this codebase resends the whole form on save, not a diff, so two
// users editing DIFFERENT fields on the same record would otherwise silently clobber each other's
// changes. `computeChangedFields` closes that gap by comparing the outgoing payload against the
// pre-edit snapshot the drawer already holds (its own prop, separate from the live-edited draft
// state) and returning only the fields that actually differ — used both to trim the outgoing
// `set` patch and to derive the `expectedValues` guard the backend checks the write against.
//
// Deliberately NOT a whole-row version token (e.g. comparing a single `updatedAt`): background
// EEL writes to OTHER fields on the same entity (interest accrual, balance updates) are frequent
// on a Loan, and would cause spurious conflicts if the guard weren't scoped to exactly the fields
// a human actually touched.

function normalize(v: unknown): unknown {
  return v === undefined || v === null || v === '' ? null : v;
}

function valuesDiffer(a: unknown, b: unknown): boolean {
  const na = normalize(a);
  const nb = normalize(b);
  if (typeof na === 'number' && typeof nb === 'number') return Math.abs(na - nb) > 1e-9;
  if (typeof na === 'object' || typeof nb === 'object') return JSON.stringify(na) !== JSON.stringify(nb);
  return na !== nb;
}

/**
 * Returns a new object containing only the entries of `payload` whose value actually differs
 * from `snapshot`'s value for that same key. Fields the payload resends unchanged are excluded —
 * only fields the human actually changed are ever part of the write or the conflict guard.
 */
export function computeChangedFields(
  snapshot: Record<string, unknown>,
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const changed: Record<string, unknown> = {};
  for (const [key, newValue] of Object.entries(payload)) {
    if (newValue === undefined) continue;
    if (!(key in snapshot)) { changed[key] = newValue; continue; }
    if (valuesDiffer(snapshot[key], newValue)) changed[key] = newValue;
  }
  return changed;
}

/**
 * True when a WS `failed` notification's raw error string signals an optimistic-lock conflict
 * (the `STALE_WRITE` code the 3 pilot handlers return), as opposed to any other failure. The WS
 * envelope's exact shape (raw handler JSON passed through unmodified, vs. reshaped by Foundations)
 * is unverified, so this matches on the structured code first and falls back to the human-readable
 * message text — the same defensive-parsing approach this codebase's own `parseEelError` already
 * uses for the 422 validation-error shape.
 */
export function isStaleWriteConflict(rawError: string): boolean {
  return rawError.includes('"code":"STALE_WRITE"') || /changed by another user/i.test(rawError);
}
